// Date helpers for the dashboard filter bar — Europe/Sofia calendar boundaries.
import dayjs from "dayjs";
import "dayjs/locale/bg";
import utc from "dayjs/plugin/utc";
import tz from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(tz);
dayjs.locale("bg");

const TZ = "Europe/Sofia";

export function todaySofia() {
  return dayjs().tz(TZ).format("YYYY-MM-DD");
}

export function shiftSofia(base, days) {
  return dayjs.tz(base, TZ).add(days, "day").format("YYYY-MM-DD");
}

export function startOfMonthSofia(base = todaySofia()) {
  return dayjs.tz(base, TZ).startOf("month").format("YYYY-MM-DD");
}

export function endOfMonthSofia(base = todaySofia()) {
  return dayjs.tz(base, TZ).endOf("month").format("YYYY-MM-DD");
}

export function startOfYearSofia(base = todaySofia()) {
  return dayjs.tz(base, TZ).startOf("year").format("YYYY-MM-DD");
}

export function daysInclusive(from, to) {
  return dayjs.tz(to, TZ).diff(dayjs.tz(from, TZ), "day") + 1;
}

// Preset id => {from, to} using inclusive Sofia calendar dates
export function computePreset(id) {
  const today = todaySofia();
  switch (id) {
    case "today":
      return { from: today, to: today };
    case "last_7":
      return { from: shiftSofia(today, -6), to: today };
    case "last_30":
      return { from: shiftSofia(today, -29), to: today };
    case "last_90":
      return { from: shiftSofia(today, -89), to: today };
    case "this_month":
      return { from: startOfMonthSofia(today), to: today };
    case "last_month": {
      const lastMonthAnchor = dayjs.tz(today, TZ).subtract(1, "month").format("YYYY-MM-DD");
      return { from: startOfMonthSofia(lastMonthAnchor), to: endOfMonthSofia(lastMonthAnchor) };
    }
    case "this_year":
      return { from: startOfYearSofia(today), to: today };
    default:
      return null;
  }
}

export function matchPreset(from, to) {
  if (!from || !to) return null;
  const ids = ["today", "last_7", "last_30", "last_90", "this_month", "last_month", "this_year"];
  for (const id of ids) {
    const p = computePreset(id);
    if (p && p.from === from && p.to === to) return id;
  }
  return "custom";
}

// Format a Sofia-local ISO date "YYYY-MM-DD" as DD.MM.YYYY
export function fmtSofiaDate(iso) {
  if (!iso) return "—";
  return dayjs.tz(iso, TZ).format("DD.MM.YYYY");
}

// For accepted-trend click-through: compute the inclusive Sofia end date of a bucket
// given the bucket's `period_start` (an ISO date-time string from the backend) and the bucket kind.
export function bucketEndSofia(periodStartIso, bucket) {
  const start = dayjs.tz(periodStartIso, TZ);
  if (bucket === "day") return start.format("YYYY-MM-DD");
  if (bucket === "week") return start.add(6, "day").format("YYYY-MM-DD");
  // month: last day of that Sofia month
  return start.endOf("month").format("YYYY-MM-DD");
}

// Format a bucket start for chart X axis labels
export function fmtBucketLabel(periodStartIso, bucket) {
  const s = dayjs.tz(periodStartIso, TZ);
  if (bucket === "day") return s.format("DD.MM");
  if (bucket === "week") return `${s.format("DD.MM")}`;
  return s.format("MMM YY");
}

// Format seconds as a compact Bulgarian duration string
export function fmtDurationSeconds(sec, t) {
  if (!sec || sec <= 0) return t("dashboard.duration_none");
  if (sec < 60) return t("dashboard.duration_seconds", { seconds: Math.round(sec) });
  const min = sec / 60;
  if (min < 60) return t("dashboard.duration_minutes", { minutes: Math.round(min) });
  const hr = min / 60;
  if (hr < 48) return t("dashboard.duration_hours", { hours: Math.round(hr) });
  const days = hr / 24;
  return t("dashboard.duration_days", { days: Math.round(days) });
}
